import { cn } from '@ifixit/ui/lib/utils';
import type { CSSProperties, ReactNode } from 'react';
import { fillStyle } from './range_slider_style';
import { useFrameThrottled } from './use_frame_throttled';

/**
 * Native-range sliders following the construction from the MooTools
 * RangeSlider replacement (#63037): overlaid `<input type="range">`
 * elements whose bodies are click-through while the thumbs stay
 * interactive, over a thumb-center-anchored track and fill. Being native
 * inputs, they're responsive, keyboard and touch accessible, and need no
 * measuring.
 *
 * Also introduced (unpolished) by #62925 for the v2 editors; whichever
 * branch merges second resolves the add/add conflict to this copy.
 */

/**
 * The 24px (THUMB_PX) hit target drawn as a 16px dot: a transparent 4px
 * border with the background clipped to the padding box. Keep in sync with
 * $rangeInputClasses in Guide/UI/templates/Guide/Intro/meta.phtml — Tailwind
 * needs both literal copies, so range_slider_style.test.ts enforces the
 * match. Exported for that test only.
 */
export const THUMB_INPUT_CLASS =
   'pointer-events-none absolute inset-0 m-0 h-full w-full appearance-none border-0 bg-transparent focus-visible:outline-none ' +
   '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:size-6 ' +
   '[&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:border-4 ' +
   '[&::-webkit-slider-thumb]:border-transparent [&::-webkit-slider-thumb]:bg-clip-padding ' +
   '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full ' +
   '[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:bg-[var(--thumb-color,var(--color-brand-500))] ' +
   '[&:hover::-webkit-slider-thumb]:ring-2 [&:hover::-webkit-slider-thumb]:ring-brand-500/40 ' +
   '[&:focus-visible::-webkit-slider-thumb]:ring-2 [&:focus-visible::-webkit-slider-thumb]:ring-offset-2 ' +
   '[&:focus-visible::-webkit-slider-thumb]:ring-brand-600 ' +
   '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:size-6 ' +
   '[&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:border-4 ' +
   '[&::-moz-range-thumb]:border-transparent [&::-moz-range-thumb]:bg-clip-padding ' +
   '[&::-moz-range-thumb]:rounded-full ' +
   '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:bg-[var(--thumb-color,var(--color-brand-500))] ' +
   '[&:hover::-moz-range-thumb]:ring-2 [&:hover::-moz-range-thumb]:ring-brand-500/40 ' +
   '[&:focus-visible::-moz-range-thumb]:ring-2 [&:focus-visible::-moz-range-thumb]:ring-offset-2 ' +
   '[&:focus-visible::-moz-range-thumb]:ring-brand-600';

function fraction(value: number, max: number): number {
   return max <= 0 ? 0 : Math.min(Math.max(value, 0), max) / max;
}

function Track({
   fillStart,
   fillEnd,
   fillFromEdge,
   fillColor,
   max,
   children,
}: {
   fillStart: number;
   fillEnd: number;
   fillFromEdge?: boolean;
   /** Overrides the fill's default brand color, e.g. a value-based hue. */
   fillColor?: string;
   max: number;
   children: ReactNode;
}) {
   const start = fraction(fillStart, max);
   const end = fraction(fillEnd, max);
   return (
      <div className="relative h-6 w-full">
         <div className="absolute inset-x-0 top-[9px] h-1.5 rounded bg-gray-300" />
         <div
            className={cn('absolute top-[9px] h-1.5 rounded', fillColor == null && 'bg-brand-500')}
            style={{
               ...fillStyle(start, end, !!fillFromEdge),
               ...(fillColor != null && { backgroundColor: fillColor }),
            }}
         />
         {children}
      </div>
   );
}

/**
 * One thumb; the fill runs from the left edge to it.
 *
 * @knipignore -- consumers arrive with the v2 editors (#62925).
 */
export function RangeSlider({
   id,
   max,
   value,
   ariaLabel,
   describedBy,
   fillColor,
   /** Formats the readout the caller already renders, e.g. "30 minutes"; reused for aria-valuetext so screen readers announce it instead of the raw number. */
   formatValue,
   onValueChange,
}: {
   id?: string;
   max: number;
   value: number;
   ariaLabel?: string;
   describedBy?: string;
   /** Overrides the brand fill, e.g. a value-based color for a score. */
   fillColor?: string;
   formatValue?: (value: number) => string;
   onValueChange: (value: number) => void;
}) {
   // A drag fires the native input event per pixel; coalescing to one
   // onValueChange per frame keeps the parent's re-renders from outrunning
   // the screen while the thumb (native input state) still tracks the
   // pointer exactly.
   const throttled = useFrameThrottled(onValueChange);
   return (
      <Track fillStart={0} fillEnd={value} fillFromEdge fillColor={fillColor} max={max}>
         <input
            id={id}
            type="range"
            min={0}
            max={max}
            step={1}
            value={value}
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            aria-valuetext={formatValue?.(value)}
            className={THUMB_INPUT_CLASS}
            // Color the thumb to match the fill; unset falls back to brand.
            style={
               fillColor == null ? undefined : ({ '--thumb-color': fillColor } as CSSProperties)
            }
            onChange={event => throttled.send(Number(event.target.value))}
            onPointerUp={throttled.flush}
            onPointerCancel={throttled.flush}
            onKeyUp={throttled.flush}
            onBlur={throttled.flush}
         />
      </Track>
   );
}

/**
 * Two thumbs; the fill spans between them.
 *
 * @knipignore -- consumers arrive with the v2 editors (#62925).
 */
export function DualRangeSlider({
   max,
   step = 1,
   low,
   high,
   lowLabel,
   highLabel,
   describedBy,
   /** Formats a thumb's readout, e.g. "20 minutes"; reused for aria-valuetext so screen readers announce it instead of the raw index. */
   formatValue,
   onValueChange,
}: {
   max: number;
   step?: number;
   low: number;
   high: number;
   lowLabel: string;
   highLabel: string;
   describedBy?: string;
   formatValue?: (value: number) => string;
   onValueChange: (low: number, high: number) => void;
}) {
   // Each thumb drags independently, so each gets its own frame coalescing;
   // see RangeSlider's useFrameThrottled comment for why.
   const lowThrottled = useFrameThrottled(onValueChange);
   const highThrottled = useFrameThrottled(onValueChange);
   const flushBoth = () => {
      lowThrottled.flush();
      highThrottled.flush();
   };
   return (
      <Track fillStart={Math.min(low, high)} fillEnd={Math.max(low, high)} max={max}>
         <input
            type="range"
            min={0}
            max={max}
            step={step}
            value={low}
            aria-label={lowLabel}
            aria-describedby={describedBy}
            aria-valuetext={formatValue?.(low)}
            className={cn(THUMB_INPUT_CLASS, 'z-10')}
            onChange={event => lowThrottled.send(Number(event.target.value), high)}
            onPointerUp={flushBoth}
            onPointerCancel={flushBoth}
            onKeyUp={flushBoth}
            onBlur={flushBoth}
         />
         <input
            type="range"
            min={0}
            max={max}
            step={step}
            value={high}
            aria-label={highLabel}
            aria-describedby={describedBy}
            aria-valuetext={formatValue?.(high)}
            className={cn(THUMB_INPUT_CLASS, 'z-20')}
            onChange={event => highThrottled.send(low, Number(event.target.value))}
            onPointerUp={flushBoth}
            onPointerCancel={flushBoth}
            onKeyUp={flushBoth}
            onBlur={flushBoth}
         />
      </Track>
   );
}
